import { Badge, Button, Text } from '@chakra-ui/react';
import { ArrowRight } from 'lucide-react';
import { OktonDataGrid, OktonDataGridColumn } from '@okton/platform-ui';
import React, { useMemo } from 'react';
import { ORIGEM_AUTOMATICA } from '../../../services/hooks/useConciliacoes';
import { UltimaConciliacao } from '../../../services/hooks/useDashboard';
import formatValue from '../../../utils/formatValue';
import { formatDataHora } from '../formatacao';
import Painel from './Painel';

interface Props {
  conciliacoes: UltimaConciliacao[];
  isLoading: boolean;
  isError: boolean;
  error?: unknown;
  onRetry: () => void;
  onVerTodas: () => void;
}

const UltimasConciliacoesTable: React.FC<Props> = ({
  conciliacoes,
  isLoading,
  isError,
  error,
  onRetry,
  onVerTodas,
}) => {
  const linhas = useMemo(
    () =>
      conciliacoes.map(c => ({
        id: c.id_cbc,
        data: formatDataHora(c.cadastro_cbc),
        conta: c.descricao_cts,
        lancamento: (
          <Text noOfLines={1} maxW="360px" title={c.descricao_exb}>
            {c.descricao_exb}
          </Text>
        ),
        valor: (
          <Text
            fontWeight="semibold"
            whiteSpace="nowrap"
            color={Number(c.valor_exb) < 0 ? 'red.500' : 'green.600'}
          >
            {formatValue(c.valor_cbc)}
          </Text>
        ),
        tipo: c.origem_cbc === ORIGEM_AUTOMATICA ? 'Automática' : 'Manual',
        usuario: c.usuario ?? '—',
        status: (
          <Badge
            colorScheme={c.divergente ? 'red' : 'green'}
            rounded="full"
            px="2"
          >
            {c.divergente ? 'Divergente' : 'Conciliado'}
          </Badge>
        ),
      })),
    [conciliacoes],
  );

  const colunas = useMemo<OktonDataGridColumn<typeof linhas[number]>[]>(
    () => [
      { key: 'data', header: 'DATA', render: linha => linha.data },
      { key: 'conta', header: 'CONTA', render: linha => linha.conta },
      {
        key: 'lancamento',
        header: 'LANÇAMENTO',
        render: linha => linha.lancamento,
      },
      { key: 'valor', header: 'VALOR', render: linha => linha.valor },
      { key: 'tipo', header: 'TIPO', render: linha => linha.tipo },
      { key: 'usuario', header: 'USUÁRIO', render: linha => linha.usuario },
      { key: 'status', header: 'STATUS', render: linha => linha.status },
    ],
    [],
  );

  return (
    <Painel
      titulo="Últimas Conciliações"
      isLoading={isLoading}
      isError={isError}
      error={error}
      onRetry={onRetry}
      acao={
        <Button
          variant="link"
          size="sm"
          colorScheme="blue"
          rightIcon={<ArrowRight size={14} />}
          onClick={onVerTodas}
        >
          Ver todas as conciliações
        </Button>
      }
    >
      <OktonDataGrid
        rows={linhas}
        columns={colunas}
        getRowId={linha => linha.id}
        hidePagination
        emptyMessage="Sem dados"
      />
    </Painel>
  );
};

export default UltimasConciliacoesTable;
